import React, { useEffect, useState } from "react";
import { toast } from "sonner";
import { Trash2, Pencil, Ticket, Percent, BadgePoundSterling, Search } from "lucide-react";
import { api } from "../../lib/api";
import { Modal, IconBtn, AddBtn, inputCls } from "./ui";

export default function Promotions({ fx = 19.5 }) {
  const [promos, setPromos] = useState([]);
  const [products, setProducts] = useState([]);
  const [form, setForm] = useState(null);

  const load = async () => {
    const { data } = await api.get("/admin/promos");
    setPromos(data);
  };
  useEffect(() => {
    load();
    api.get("/admin/products").then((r) => setProducts(r.data || [])).catch(() => {});
  }, []);

  const del = async (id) => {
    if (!window.confirm("Delete this promo code?")) return;
    await api.delete(`/admin/promos/${id}`);
    toast.success("Promo deleted");
    load();
  };

  const fmtValue = (p) => (p.type === "percent" ? `${p.value}% off` : `GH₵${p.value} off`);

  return (
    <div data-testid="admin-promotions">
      <div className="flex justify-between items-center gap-2 mb-4">
        <p className="text-sm text-[#5C5C5C]">Create discount codes and choose exactly where they apply.</p>
        <AddBtn label="New promo code" onClick={() => setForm({})} testid="add-promo-btn" />
      </div>
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
        {promos.length === 0 && <p className="text-[#5C5C5C]">No promo codes yet.</p>}
        {promos.map((p) => {
          const expired = p.expires_at && new Date(p.expires_at) < new Date();
          const maxed = p.usage_limit > 0 && (p.used_count || 0) >= p.usage_limit;
          const live = p.active && !expired && !maxed;
          return (
            <div key={p.id} className="bg-white border border-black/5 rounded-2xl p-5" data-testid={`promo-row-${p.code}`}>
              <div className="flex items-start justify-between gap-2">
                <div className="flex items-center gap-2 min-w-0">
                  <span className="w-9 h-9 rounded-full bg-[#306D29]/10 text-[#306D29] flex items-center justify-center shrink-0">
                    {p.type === "percent" ? <Percent size={16} /> : <BadgePoundSterling size={16} />}
                  </span>
                  <div className="min-w-0">
                    <p className="font-display text-lg text-[#0D530E] truncate">{p.code}</p>
                    <p className="text-xs text-[#5C5C5C]">{fmtValue(p)}</p>
                  </div>
                </div>
                <div className="flex gap-0.5 shrink-0">
                  <IconBtn title="Edit" testid={`promo-edit-${p.code}`} onClick={() => setForm(p)}><Pencil size={15} /></IconBtn>
                  <IconBtn title="Delete" danger testid={`promo-del-${p.code}`} onClick={() => del(p.id)}><Trash2 size={15} /></IconBtn>
                </div>
              </div>
              <div className="flex flex-wrap gap-1.5 mt-4 text-xs">
                <span className={`rounded-full px-2.5 py-1 font-semibold ${live ? "bg-[#306D29]/10 text-[#306D29]" : "bg-black/5 text-[#5C5C5C]"}`}>
                  {live ? "Live" : expired ? "Expired" : maxed ? "Limit reached" : "Inactive"}
                </span>
                <span className="bg-[#E7E1B1]/60 text-[#0D530E] rounded-full px-2.5 py-1">
                  {p.scope === "selected" ? `${(p.product_ids || []).length} products` : "All products"}
                </span>
                {p.min_order_ghs > 0 && <span className="bg-[#E7E1B1]/60 text-[#0D530E] rounded-full px-2.5 py-1">Min GH₵{p.min_order_ghs}</span>}
                {p.usage_limit > 0 && <span className="bg-[#E7E1B1]/60 text-[#0D530E] rounded-full px-2.5 py-1">{p.used_count || 0}/{p.usage_limit} used</span>}
                {p.expires_at && <span className="bg-[#E7E1B1]/60 text-[#0D530E] rounded-full px-2.5 py-1">Ends {new Date(p.expires_at).toLocaleDateString()}</span>}
              </div>
            </div>
          );
        })}
      </div>
      {form && <PromoForm promo={form} products={products} onClose={() => setForm(null)} onSaved={() => { setForm(null); load(); }} />}
    </div>
  );
}

function PromoForm({ promo, products, onClose, onSaved }) {
  const editing = !!promo.id;
  const [f, setF] = useState({
    code: promo.code || "",
    type: promo.type || "percent",
    value: promo.value ?? 10,
    scope: promo.scope || "all",
    product_ids: promo.product_ids || [],
    active: promo.active ?? true,
    expires_at: promo.expires_at ? promo.expires_at.slice(0, 10) : "",
    usage_limit: promo.usage_limit || 0,
    min_order_ghs: promo.min_order_ghs || 0,
  });
  const [q, setQ] = useState("");
  const [saving, setSaving] = useState(false);
  const set = (k, v) => setF((s) => ({ ...s, [k]: v }));

  const toggle = (id) => set("product_ids", f.product_ids.includes(id) ? f.product_ids.filter((x) => x !== id) : [...f.product_ids, id]);

  const filtered = products.filter((p) => p.name.toLowerCase().includes(q.toLowerCase()));

  const submit = async (e) => {
    e.preventDefault();
    if (f.scope === "selected" && f.product_ids.length === 0) { toast.error("Pick at least one product or use 'All products'"); return; }
    setSaving(true);
    const body = {
      code: f.code.trim().toUpperCase(),
      type: f.type,
      value: Number(f.value),
      scope: f.scope,
      product_ids: f.scope === "selected" ? f.product_ids : [],
      active: f.active,
      expires_at: f.expires_at || "",
      usage_limit: Number(f.usage_limit) || 0,
      min_order_ghs: Number(f.min_order_ghs) || 0,
    };
    try {
      if (editing) await api.put(`/admin/promos/${promo.id}`, body);
      else await api.post("/admin/promos", body);
      toast.success(editing ? "Promo saved" : "Promo created");
      onSaved();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to save promo");
      setSaving(false);
    }
  };

  const pill = (val, cur, onClick, label) => (
    <button type="button" onClick={onClick}
      className={`flex-1 rounded-xl border px-3 py-2.5 text-sm font-semibold ${cur === val ? "border-[#306D29] bg-[#306D29]/5 text-[#0D530E]" : "border-black/10 bg-white text-[#5C5C5C]"}`}
      style={{ transition: "border-color 0.15s ease" }} data-testid={`promo-${label}`}>{label}</button>
  );

  return (
    <Modal title={editing ? "Edit promo code" : "New promo code"} onClose={onClose} testid="promo-form">
      <form onSubmit={submit} className="space-y-4">
        <div>
          <label className="text-xs font-semibold text-[#5C5C5C]">Code</label>
          <input required className={`${inputCls} uppercase`} placeholder="WELCOME10" value={f.code} onChange={(e) => set("code", e.target.value)} data-testid="pf-code" />
        </div>

        <div>
          <label className="text-xs font-semibold text-[#5C5C5C] block mb-1">Discount type</label>
          <div className="flex gap-2">
            {pill("percent", f.type, () => set("type", "percent"), "percent")}
            {pill("fixed", f.type, () => set("type", "fixed"), "fixed")}
          </div>
        </div>
        <div>
          <label className="text-xs font-semibold text-[#5C5C5C]">{f.type === "percent" ? "Percentage off (%)" : "Amount off (GH₵)"}</label>
          <input required type="number" min="0" step="0.01" className={inputCls} value={f.value} onChange={(e) => set("value", e.target.value)} data-testid="pf-value" />
        </div>

        <div>
          <label className="text-xs font-semibold text-[#5C5C5C] block mb-1">Applies to</label>
          <div className="flex gap-2">
            {pill("all", f.scope, () => set("scope", "all"), "all")}
            {pill("selected", f.scope, () => set("scope", "selected"), "selected")}
          </div>
        </div>

        {f.scope === "selected" && (
          <div className="border border-black/10 rounded-xl p-3 bg-white" data-testid="pf-products">
            <div className="relative mb-2">
              <Search size={15} className="absolute left-3 top-1/2 -translate-y-1/2 text-[#5C5C5C]" />
              <input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search products" className="w-full bg-[#FBF5DD]/60 rounded-lg pl-9 pr-3 py-2 text-sm focus:outline-none" />
            </div>
            <p className="text-xs text-[#5C5C5C] mb-2">{f.product_ids.length} selected</p>
            <div className="max-h-52 overflow-y-auto space-y-1">
              {filtered.slice(0, 200).map((p) => (
                <label key={p.id} className="flex items-center gap-2 text-sm py-1 px-1 rounded hover:bg-[#E7E1B1]/40 cursor-pointer" data-testid={`pf-product-${p.id}`}>
                  <input type="checkbox" checked={f.product_ids.includes(p.id)} onChange={() => toggle(p.id)} className="accent-[#306D29]" />
                  <span className="truncate">{p.name}</span>
                </label>
              ))}
              {filtered.length === 0 && <p className="text-xs text-[#5C5C5C] py-2">No products match.</p>}
            </div>
          </div>
        )}

        <div className="grid grid-cols-2 gap-3">
          <div>
            <label className="text-xs font-semibold text-[#5C5C5C]">Expiry date (optional)</label>
            <input type="date" className={inputCls} value={f.expires_at} onChange={(e) => set("expires_at", e.target.value)} data-testid="pf-expiry" />
          </div>
          <div>
            <label className="text-xs font-semibold text-[#5C5C5C]">Usage limit (0 = ∞)</label>
            <input type="number" min="0" className={inputCls} value={f.usage_limit} onChange={(e) => set("usage_limit", e.target.value)} data-testid="pf-limit" />
          </div>
        </div>
        <div>
          <label className="text-xs font-semibold text-[#5C5C5C]">Minimum order (GH₵, 0 = none)</label>
          <input type="number" min="0" className={inputCls} value={f.min_order_ghs} onChange={(e) => set("min_order_ghs", e.target.value)} data-testid="pf-minorder" />
        </div>

        <label className="flex items-center gap-2 text-sm font-medium text-[#0D530E]">
          <input type="checkbox" checked={f.active} onChange={(e) => set("active", e.target.checked)} className="accent-[#306D29]" data-testid="pf-active" />
          Active
        </label>

        <button type="submit" disabled={saving} data-testid="pf-submit"
          className="w-full bg-[#306D29] hover:bg-[#0D530E] text-white font-semibold py-3 rounded-full disabled:opacity-60" style={{ transition: "background-color 0.2s ease" }}>
          {saving ? "Saving…" : editing ? "Save promo" : "Create promo"}
        </button>
      </form>
    </Modal>
  );
}
